import CustomError from "@/framework/errors/CustomError";
import GraphqlApiError from "@/framework/network/gql/GraphqlApiError";
import { ErrorLogService } from "@/framework/logger/errorLog/errorLogService";
import { AuthenticationRepository } from "@/framework/repositories/authenticationRepository";
import { i18n } from "@/framework/plugins/i18n";

/**
 * Represents BE Error format
 */
export interface CustomBEError {
  error_id: string;
  error_code: string;
  error_type: "UnknownErrorFormat" | "KotoznaErrorFormat" | "GraphQLError";
  error_message_code?: string;
  locations: [];
  message: string;
  path: string[] | null;
  traceback?: string | null;
}

export class ErrorService {
  private _errorLog: ErrorLogService;

  public constructor(errorLog: ErrorLogService = new ErrorLogService()) {
    this._errorLog = errorLog;
  }

  /**
   * Handles an error and returns a CustomError
   * @param error
   * Graphql api error example
   * {
   *  "type": "badRequest",
   *  "apolloError": {
   *    "graphQLErrors": [],
   *    "clientErrors": [],
   *    "networkError": {
   *      "name": "ServerError",
   *      "response": {
   *      },
   *      "statusCode": 400,
   *      "result": {
   *        "data": null,
   *        "errors": [
   *          {
   *            "error_code": "GraphQLError",
   *            "error_id": "012f-dc6c-fb3d-bd9c-1ab3-94dc-945a-e2c9",
   *            "error_type": "GraphQLError",
   *            "locations": [
   *              [
   *                2,
   *                3
   *              ]
   *            ],
   *            "message": "Cannot query field 'listResponders1' on type 'Query'. Did you mean 'listResponders' or 'listRespondersAdmin'?\n\nGraphQL request:2:3\n1 | query listResponders {\n2 |   listResponders1 {\n  |   ^\n3 |     tenant_id",
   *            "path": null,
   *            "traceback": ""
   *          }
   *        ]
   *      }
   *    },
   *    "message": "Response not successful: Received status code 400"
   *  },
   *  "errorMessage": "Response not successful: Received status code 400",
   *  "operationName": "listResponders",
   *  "variables": {
   *  },
   *  "name": "GraphqlApiError"
   * }
   */
  handleError = async (error: any) => {
    try {
      console.warn("error");
      console.warn(JSON.stringify(error));
      let isZodError = error && error.name == "ZodError";
      let isGraphqlApiError = error && error.name == "GraphqlApiError";

      if (isZodError) {
        let message = this.formatIssuesToString(error.issues);
        return new CustomError().setErrorMessageControls(message, error.name, error.name, "");
      }

      if (isGraphqlApiError) {
        let e = error as GraphqlApiError;
        console.warn(JSON.stringify(error));

        if (e.operationName == "importProcess") {
          return;
        }

        // Logout user if unauthorized
        if (e.type == GraphqlApiError.Type.unauthorized) {
          await new AuthenticationRepository().signOutUser(false);
        }

        if (e.type == GraphqlApiError.Type.badRequest || e.type == GraphqlApiError.Type.internalServerError) {
          const result = JSON.parse(JSON.stringify(error.apolloError.networkError.result));
          const firstError = result.errors[0] as CustomBEError;
          let message = `\nPath: ${error.operationName}\nError id: ${firstError.error_id}\nError type: ${firstError.error_type}\nError: ${firstError.message}`;
          return new CustomError().setErrorMessageControls(message, error.type, error.name, error.errorMessage);
        }

        if (e.type == GraphqlApiError.Type.graphqlFailed) {
          const firstError = error.apolloError.graphQLErrors[0] as CustomBEError;
          let message = `\nPath: ${error.operationName}\nError id: ${firstError.error_id}\nError type: ${firstError.error_type}\nError: ${firstError.message}`;
          // KTZNCHAT-14080 Hide BE deadlock error with a "try again later" message
          if (firstError.error_message_code == "ERR_ALREADY_IN_PROGRESS") {
            return new CustomError().setErrorMessageControls(
              message,
              error.type,
              error.name,
              i18n.t("error_already_in_progress").toString()
            );
          }
          // KTZNCHAT-14078 Show custom error for error code: 'ERR_DATA_NOT_FOUND' during sandbox apply
          if (
            firstError.error_message_code == "ERR_DATA_NOT_FOUND" &&
            firstError.path &&
            firstError.path[0] === "applySandbox"
          ) {
            return new CustomError().setErrorMessageControls(
              i18n.t("no_sandbox_error_message").toString(),
              "", // errorType -> set to blank
              i18n.t("no_sandbox_error_title").toString(),
              "" // text -> set to blank
            );
          }
          return new CustomError().setErrorMessageControls(message, error.type, error.name, error.errorMessage);
        }

        if (e.type == GraphqlApiError.Type.parseError) {
          return new CustomError().setErrorMessageControls("", error.type, error.name, error.errorMessage);
        }
      }

      if (!isZodError && !isGraphqlApiError) {
        const variables = JSON.stringify(error.variables, null, "\t");
        const customText = JSON.stringify(error, null, "\t");
        await this._errorLog.sendLog(error.operationName, variables, customText, "");
        return new CustomError().setErrorMessageControls(customText, error.type, error.name, error.errorMessage);
      }
    } catch (e) {
      await this._errorLog.sendLog(
        "Exception occurred while handling error",
        "",
        JSON.stringify(error),
        JSON.stringify(e)
      );
      // If error object is empty, don't show a custom error
      if (Object.keys(e as object).length === 0) return;
      // Return custom error
      return new CustomError().setErrorMessageControls(
        "Exception occurred while handling error",
        "",
        "",
        JSON.stringify(e)
      );
    }
  };

  /**
   * Zod: Formats the issues array to a string
   * @param issueData
   * @data format will be
   * {
   *   "issues": [
   *   {
   *     "code": "invalid_type",
   *     "expected": "number",
   *     "received": "string",
   *     "path": [
   *       "CopyAbleTenants",
   *       0,
   *       "code"
   *     ],
   *     "message": "Expected number, received string"
   *   },
   *   {
   *     "code": "invalid_type",
   *     "expected": "number",
   *     "received": "string",
   *     "path": [
   *       "CopyAbleTenants",
   *       1,
   *       "code"
   *     ],
   *     "message": "Expected number, received string"
   *   },
   * ],
   *   "name": "ZodError"
   * }
   */
  private formatIssuesToString(issueData: any) {
    const formattedMessages = issueData.map((issue: { path: any[]; message: any }) => {
      const path = issue.path.join(" -> ");
      const message = issue.message;
      return `Path '${path}': ${message}`;
    });
    return formattedMessages.join("\n");
  }
}
